/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect } from "react";
import classNames from "classnames";
import { uniqueId } from "lodash";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { BiLogOut } from "react-icons/bi";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import usePark from "common/hooks/usePark";

import {
  IMenuConf,
  superadminRootMenu,
  getSuperadminFilteredMenu,
} from "conf/menu";

import ParkSelect from "components/select/ParkSelect/ParkSelect";
import useAuth from "common/hooks/useAuth";
import useRouter from "common/hooks/use-router";
import { getParkConfiguration } from "types/Park";
import { useAppConfig } from "common/providers/AppConfigProvider";

interface IMenu {
  currentId: string;
  setSidebarOpen?: any;
}

const menuStyle = "text-white font-medium hover:bg-green-500/30";

// Un item est actif si le chemin courant correspond exactement à sa route ou à
// une de ses sous-routes. La comparaison se fait segment par segment pour
// éviter qu'un id soit un simple préfixe d'un autre
// (ex. "balance-sheets" vs "saved-balance-sheets").
const isMenuItemCurrent = (pathname: string, search: string, id: string) => {
  if (id === "./") {
    return pathname === "./" || pathname === "/";
  }

  const [idPath, idSearch] = id.split("?");
  const target = `/${idPath}`;

  if (pathname !== target && !pathname.startsWith(`${target}/`)) {
    return false;
  }

  if (!idSearch) {
    return true;
  }

  // Certains items pointent vers la même page avec des filtres différents
  // (ex. "imports?type=zims" et "imports?type=observationGrid").
  const currentSearch = new URLSearchParams(search);
  return Array.from(new URLSearchParams(idSearch).entries()).every(
    ([key, value]) => currentSearch.get(key) === value
  );
};

function Menu({ currentId, setSidebarOpen }: IMenu) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { navigate } = useRouter();
  const { user: currentUser, signout } = useAuth();
  const { isLicenceValid } = useAppConfig();

  const { refreshPark, park } = usePark();

  // Menu items that are ALWAYS accessible (even without valid license)
  const alwaysAccessibleIds = [
    "./",
    "users",
    "my-account",
    "servers",
    "support",
    "parks",
    "login",
  ];

  const handleParkChange = useCallback(
    async (id: any, row: any) => {
      await refreshPark(row?.value || null);
      navigate("/");
    },
    [navigate, refreshPark]
  );

  useEffect(() => {
    const effect = async () => {
      if (currentUser && !currentUser.isAdmin && !currentUser.isSuperadmin) {
        await refreshPark(currentUser?.park?.id);
      }
    };
    effect();
  }, [currentUser, refreshPark]);

  const renderMenu = (currentMenu: IMenuConf) => {
    return Object.keys(currentMenu).map((key: string, index: number) => {
      const { label, children, ignoreGroup } = currentMenu[key];

      const childrenList = children?.map(
        ({ id, Icon, label: childLabel, permissionsAccessKeyForManagers }) => {
          const isCurrent = isMenuItemCurrent(currentId ?? "", search, id);
          const isAccessible = alwaysAccessibleIds.includes(id);
          const isBlocked = !isLicenceValid && !isAccessible;

          const hasPermission =
            (currentUser?.role?.name !== "Manager" &&
              currentUser?.role?.name !== "Observer") ||
            ((currentUser?.role?.name === "Manager" ||
              currentUser?.role?.name === "Observer") &&
              (!permissionsAccessKeyForManagers ||
                (
                  (currentUser?.administrationPermissions as any)?.[
                    permissionsAccessKeyForManagers
                  ] as any
                )?.read));

          if (!hasPermission) {
            return undefined;
          }

          // If blocked by license, render as disabled
          if (isBlocked) {
            return (
              <div
                key={id}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md opacity-40 cursor-not-allowed relative"
                title={t("license.featureBlockedTooltip")}
              >
                <Icon
                  className="text-white mr-3 flex-shrink-0 h-4 w-4"
                  aria-hidden="true"
                />
                {t(`titlePages.${childLabel}`)}
                <LockClosedIcon className="h-3 w-3 ml-auto text-red-400" />
              </div>
            );
          }

          return (
            <Link
              key={id}
              to={`/${id}`}
              className={classNames(
                isCurrent ? "bg-gray-100 text-gray-900" : menuStyle,
                "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
              )}
              onClick={setSidebarOpen ? () => setSidebarOpen(false) : () => {}}
            >
              <Icon
                className={classNames(
                  isCurrent
                    ? "text-orange-500"
                    : "text-white group-hover:text-ternary",
                  "mr-3 flex-shrink-0 h-4 w-4"
                )}
                aria-hidden="true"
              />

              {t(`titlePages.${childLabel}`)}
            </Link>
          );
        }
      );
      return ignoreGroup || !childrenList?.length ? (
        <div
          className="mt-4 mb-2 text-sm text-white font-semibold"
          key={`menu-${index}`}
        >
          {childrenList}
        </div>
      ) : (
        <div key={`menu-group${index}`}>
          <div
            className="mt-4 mb-2 text-sm text-white font-semibold"
            key={`menu-${index}`}
          >
            {t(`titleDividers.${label}`)}
          </div>
          {childrenList}
        </div>
      );
    });
  };

  return (
    <div className="sidebar rounded-lg flex flex-col flex-1 h-full">
      <div className="flex flex-col px-4 pt-0 md:pt-6 pb-0 md:pb-1 ">
        {park && park.logo && (
          <div className="h-[80px]">
            <img
              className="mx-auto w-auto object-contain"
              src={`${import.meta.env.BASE_URL}images/logoWhite.png`}
              alt="Logo"
            />
          </div>
        )}

        <div className="flex items-center gap-4 my-4">
          <p className="text-base text-white text-pretty break-words m-0 max-w-[calc(100%-5rem)]">
            {t("common.welcome")}
            <b> {currentUser?.username}</b>
          </p>
        </div>

        {
          // keep this condition for later
          // currentUser?.isSuperadmin && !isTrue("VITE_IS_DEDICATED_APP") && (
          <ParkSelect
            className="parkMenuSelect bg-white rounded-lg"
            id={uniqueId()}
            clearable={currentUser?.isSuperadmin}
            disabled={!currentUser?.isSuperadmin}
            onChange={handleParkChange}
            value={park?.id}
          />
          /* ) */
        }
      </div>

      <div
        className="overflow-y-auto flex-1 px-4 pb-6
        scrollbar-thin scrollbar-thumb-slate-800/90 scrollbar-track-slate-200 scrollbar-thumb-rounded scrollbar-track-rounded"
      >
        {renderMenu(
          park?.id
            ? getSuperadminFilteredMenu(getParkConfiguration(park))
            : superadminRootMenu
        )}

        <button
          type="button"
          className="group flex items-center px-2 py-2 text-sm font-medium rounded-md mt-2  text-white opacity-40 hover:bg-gray-500/30 hover:opacity-100
        w-full "
          onClick={() => {
            signout();
            navigate("/login");
          }}
        >
          <BiLogOut
            className={classNames(
              "text-white group-hover:text-ternary",
              "mr-3 flex-shrink-0 h-4 w-4"
            )}
            aria-hidden="true"
          />
          {t("titlePages.logout")}
        </button>
      </div>
    </div>
  );
}

export default Menu;
